// Print and HTML-export documents for the BOPPPS planner.
//
// Pulled out of the component so the two documents can be tested directly:
// they are the artefacts instructors actually hand in, and the formatting
// editor and file attachments both have to leave them clean.
//
// Every field goes through `fieldToHtml`, which returns either the field's
// rich HTML or its plain text with the newlines turned into <br> — so a plan
// written before the editor existed prints exactly as it always did, and one
// written with it prints the bold, the bullets and the headings.
//
// Rich HTML is sanitized here as well as at render time. These documents are
// written into a new window and downloaded as files, which is outside React's
// tree and therefore outside SafeHtml's reach.

import { sanitizeHtml } from '../../../utils/sanitizeHtml';
import { formatTotalActivityTime } from './duration';
import { escapeHtml, fieldToHtml, getRichText } from './plannerText';
import { summarizeReferenceFiles, KIND_LABELS } from './referenceFiles';

/**
 * Formatting the editor can produce, restated for documents that do not load
 * the app's stylesheet. Both templates reset margins with `* { margin: 0 }`,
 * which would otherwise flatten every list and paragraph the editor made.
 */
const richContentCss = (scale = 1) => `
        .rich p { margin: 0 0 ${0.5 * scale}em; }
        .rich p:last-child { margin-bottom: 0; }
        .rich ul, .rich ol { margin: 0 0 ${0.5 * scale}em; padding-left: 1.5em; }
        .rich ul { list-style: disc outside; }
        .rich ol { list-style: decimal outside; }
        .rich li { margin-bottom: ${0.2 * scale}em; }
        .rich li > p { margin: 0; }
        .rich h1, .rich h2, .rich h3, .rich h4, .rich h5, .rich h6 {
            font-weight: 700; margin: ${0.4 * scale}em 0 ${0.25 * scale}em; line-height: 1.25;
        }
        .rich h1 { font-size: 1.4em; }
        .rich h2 { font-size: 1.25em; }
        .rich h3 { font-size: 1.1em; }
        .rich h4, .rich h5, .rich h6 { font-size: 1em; }
        .rich strong, .rich b { font-weight: 700; }
        .rich em, .rich i { font-style: italic; }
        .rich u { text-decoration: underline; }
        .rich s, .rich del { text-decoration: line-through; }
        .rich blockquote {
            margin: 0 0 ${0.5 * scale}em; padding-left: 0.75em; border-left: 3px solid #d1d5db; color: #4b5563;
        }
        .rich a { color: #1d4ed8; text-decoration: underline; }
        .rich code { font-family: ui-monospace, Menlo, Consolas, monospace; font-size: 0.95em; }
        .rich mark { background: #fef08a; }
        .rich table { border-collapse: collapse; margin-bottom: ${0.5 * scale}em; }
        .rich td, .rich th { border: 1px solid #d1d5db; padding: 0.25em 0.4em; }
        .rich img { max-width: 100%; height: auto; }
        .rich > :first-child { margin-top: 0; }`;

/**
 * One field as document-ready HTML: rich content sanitized, plain text escaped
 * with its line breaks preserved.
 */
export const renderField = (holder, field, plainValue) => {
  const plain = plainValue === undefined ? (holder ? holder[field] : '') : plainValue;
  const html = fieldToHtml(plain, getRichText(holder, field));
  return html ? sanitizeHtml(html) : '';
};

/** One labelled (or bare) half of a split cell. */
const half = (html, { label, learner, labelled }) => {
  const classes = `activity-half${learner ? ' activity-half-learner' : ''}`;
  const heading = labelled ? `<span class="activity-half-label">${label}</span>` : '';
  return `<div class="${classes}">${heading}<div class="rich">${html}</div></div>`;
};

/**
 * The activity's name, when it has one. A plan long enough to need names is a
 * plan whose printed sheet is hard to scan without them; a plan that has none
 * prints exactly as it did before the field existed.
 */
const activityTitle = (activity) => {
  const title = activity && typeof activity.title === 'string' ? activity.title.trim() : '';
  return title ? `<div class="activity-title">${escapeHtml(title)}</div>` : '';
};

/**
 * The two halves of an activity cell: the instructor's notes above what the
 * students do. Each is labelled, because the whole reason they are separate
 * fields is that they are read by different people — an unlabelled run of two
 * paragraphs on the printed sheet would put the separation straight back.
 *
 * A half with nothing in it is left out rather than printing an empty label.
 * On a student handout the instructor's half is dropped entirely, and the
 * remaining half loses its label: on a sheet where everything is for the
 * students, "STUDENTS" over every row is noise.
 */
const activityHalves = (activity, options = {}) => {
  const handout = Boolean(options.studentHandout);
  const instructor = handout ? '' : renderField(activity, 'facilitatorActivity');
  const learner = renderField(activity, 'learnerActivity');
  const parts = [];
  if (instructor) parts.push(half(instructor, { label: 'Instructor', labelled: true }));
  if (learner) parts.push(half(learner, { label: 'Students', learner: true, labelled: !handout }));
  return parts.join('');
};

/**
 * The materials cell, split the same way: what the instructor sets up versus
 * what the students need in front of them.
 *
 * Unlike the activity cell these are labelled only when both halves are
 * filled. Every plan written before the split has one materials list, and a
 * bare "INSTRUCTOR" over each of them in a 150px column is noise, not
 * information.
 */
const materialsHalves = (activity, options = {}) => {
  const handout = Boolean(options.studentHandout);
  const instructor = handout ? '' : renderField(activity, 'materialsRequired');
  const learner = renderField(activity, 'learnerMaterials');
  const labelled = Boolean(instructor) && Boolean(learner);
  const parts = [];
  if (instructor) parts.push(half(instructor, { label: 'Instructor', labelled }));
  if (learner) parts.push(half(learner, { label: 'Students', learner: labelled, labelled }));
  return parts.join('');
};

/**
 * The attachment line that appears under an activity in both documents. It
 * names the instructor's source material, so it stays off a student handout.
 */
const referenceLine = (activity, options = {}) => {
  if (options.studentHandout) return '';
  const files = Array.isArray(activity && activity.referenceFiles) ? activity.referenceFiles : [];
  if (!files.length) return '';
  return `<div class="reference-note"><strong>Reference material:</strong> ${escapeHtml(summarizeReferenceFiles(files))}</div>`;
};

/**
 * The reference material itself, as an appendix at the end of the HTML export.
 *
 * The lesson plan above it stays clean — the appendix is a separate section
 * after the table — but the text travels with the file, which is the point of
 * attaching it: the export can be handed to an assistant as a single document.
 */
const referenceAppendix = (activities = []) => {
  const sections = [];
  activities.forEach((activity, index) => {
    const files = Array.isArray(activity && activity.referenceFiles) ? activity.referenceFiles : [];
    files.forEach((file) => {
      // A link's address is the reference; its "text" is the instructor's note
      // about it. `file.url` has already been through normalizeReferenceUrl —
      // http(s) only — so it is safe to write as an anchor.
      const address = file.kind === 'link' && file.url
        ? `<p class="reference-link"><a href="${escapeHtml(file.url)}" target="_blank" rel="noopener noreferrer">${escapeHtml(file.url)}</a></p>`
        : '';
      const body = file.text
        ? `<pre class="reference-text">${escapeHtml(file.text)}</pre>`
        : (address
          ? ''
          : `<p class="reference-missing">${escapeHtml(file.note || 'No text could be extracted from this file.')}</p>`);
      sections.push(`
                <article class="reference-item">
                    <h3>${escapeHtml(file.name)}</h3>
                    <p class="reference-meta">Activity ${index + 1} · ${escapeHtml(KIND_LABELS[file.kind] || 'File')}${file.truncated ? ' · truncated' : ''}</p>
                    ${address}${body}
                </article>`);
    });
  });

  if (!sections.length) return '';
  return `
        <section class="reference-appendix">
            <h2>Reference material</h2>
            <p class="reference-intro">Source material attached to the activities above, extracted as text.</p>
            ${sections.join('')}
        </section>`;
};

/**
 * The landscape print sheet — the one instructors hand in.
 *
 * With `options.studentHandout` it becomes the sheet the students get: the
 * instructor's notes, their materials, their reference material and their
 * reflections all come out, leaving the plan the class actually follows.
 */
export const buildPrintDocument = (lessonData = {}, options = {}) => {
  const title = options.title || lessonData.lessonTopic || 'Untitled';
  const activities = Array.isArray(lessonData.activities) ? lessonData.activities : [];
  const handout = Boolean(options.studentHandout);

  return `<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8">
    <title>BOPPPS Lesson Plan - ${escapeHtml(title)}</title>
    <style>
        @page { size: landscape; margin: 0.5in; }
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: Arial, sans-serif; font-size: 11px; line-height: 1.2; color: #000; }
        .header { text-align: center; margin-bottom: 10px; border-bottom: 2px solid #000; padding-bottom: 5px; }
        .header h1 { font-size: 16px; font-weight: bold; margin-bottom: 2px; }
        table { width: 100%; border-collapse: collapse; border: 2px solid #000; }
        td, th { border: 1px solid #000; padding: 4px; vertical-align: top; text-align: left; }
        .section-header { background-color: #f0f0f0; font-weight: bold; width: 120px; font-size: 10px; }
        .black-header { background-color: #000; color: #fff; font-weight: bold; text-align: center; font-size: 11px; }
        .sub-header { background-color: #f0f0f0; font-weight: bold; font-size: 10px; text-align: center; }
        .content { font-size: 10px; line-height: 1.3; }
        .activity-row { height: 60px; }
        .duration-col { width: 60px; text-align: center; }
        .materials-col { width: 150px; }
        .objectives-col { width: 60%; }
        .pre-assessment-col { width: 20%; }
        .post-assessment-col { width: 60%; }
        .summary-col { width: 20%; }
        .reference-note { margin-top: 4px; font-size: 9px; color: #374151; }
        .activity-half { margin-bottom: 5px; }
        .activity-half:last-child { margin-bottom: 0; }
        .activity-half-learner { border-top: 1px dotted #9ca3af; padding-top: 4px; }
        .activity-half-label {
            display: block; font-size: 8px; font-weight: bold; text-transform: uppercase;
            letter-spacing: 0.04em; color: #4b5563;
        }
        .activity-title { font-weight: bold; font-size: 10px; margin-bottom: 3px; }
        .total-row { background-color: #f0f0f0; font-size: 10px; }
        .total-row .duration-col { font-size: 10px; }
${richContentCss(1)}
    </style>
</head>
<body>
    <div class="header">
        <h1>BOPPPS Lesson Planning Template</h1>
    </div>

    <table>
        <tr>
            <td class="section-header">Lesson Topic</td>
            <td class="content rich" colspan="3">${escapeHtml(lessonData.lessonTopic || '')}</td>
        </tr>

        <tr>
            <td class="section-header">Bridge-In</td>
            <td class="content rich" colspan="3">${renderField(lessonData, 'bridgeIn')}</td>
        </tr>

        <tr>
            <td class="section-header">Objective(s)</td>
            <td class="content rich objectives-col">${renderField(lessonData, 'objective')}</td>
            <td class="section-header" style="width: 120px;">Pre-Assessment</td>
            <td class="content rich pre-assessment-col">${renderField(lessonData, 'preAssessment')}</td>
        </tr>

        <tr>
            <td class="black-header" colspan="4">Presentation/Practice/Participation</td>
        </tr>

        <tr>
            <td class="sub-header duration-col">Duration<br>(in min.)</td>
            <td class="sub-header" colspan="2">${handout ? 'Learner Activity' : 'Facilitator or Learner Activity (add rows as needed)'}</td>
            <td class="sub-header materials-col">Materials Required</td>
        </tr>
${activities.map((activity) => `
        <tr class="activity-row">
            <td class="content duration-col">${escapeHtml(activity.duration || '')}</td>
            <td class="content" colspan="2">${activityTitle(activity)}${activityHalves(activity, options)}${referenceLine(activity, options)}</td>
            <td class="content materials-col">${materialsHalves(activity, options)}</td>
        </tr>`).join('')}

        <tr class="total-row">
            <td class="duration-col"><strong>Total</strong><br>${escapeHtml(formatTotalActivityTime(activities))}</td>
            <td class="content" colspan="2">Total planned activity time</td>
            <td class="materials-col"></td>
        </tr>

        <tr>
            <td class="section-header">Post-Assessment</td>
            <td class="content rich post-assessment-col">${renderField(lessonData, 'postAssessment')}</td>
            <td class="section-header" style="width: 120px;">Summary</td>
            <td class="content rich summary-col">${renderField(lessonData, 'summary')}</td>
        </tr>

${handout ? '' : `
        <tr>
            <td class="section-header">Reflections on the Lesson</td>
            <td class="content rich" colspan="3">${renderField(lessonData, 'reflections')}</td>
        </tr>`}
    </table>
</body>
</html>`;
};

/**
 * The shareable HTML file, which also carries the reference material.
 *
 * `options.studentHandout` makes the same document student-facing: instructor
 * notes and materials, the reference material (line and appendix) and the
 * reflections all come out.
 */
export const buildExportDocument = (lessonData = {}, options = {}) => {
  const title = options.title || lessonData.lessonTopic || 'BOPPPS Lesson Planning Template';
  const activities = Array.isArray(lessonData.activities) ? lessonData.activities : [];
  const handout = Boolean(options.studentHandout);

  return `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="utf-8">
    <meta name="viewport" content="width=device-width, initial-scale=1">
    <title>BOPPPS Lesson Plan - ${escapeHtml(title)}</title>
    <style>
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', sans-serif;
            line-height: 1.4; color: #333; background-color: #f9fafb; padding: 20px;
        }
        .container {
            max-width: 1200px; margin: 0 auto; background: white; border-radius: 8px;
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1); overflow: hidden;
        }
        .header { text-align: center; padding: 20px; background-color: white; border-bottom: 1px solid #e5e7eb; }
        .header h1 { font-size: 28px; font-weight: bold; margin-bottom: 8px; color: #1f2937; }
        .table-container { border: 1px solid #d1d5db; }
        .row { display: flex; border-bottom: 1px solid #d1d5db; min-height: 60px; }
        .row:last-child { border-bottom: none; }
        .section-header {
            background-color: #f3f4f6; font-weight: bold; padding: 12px; border-right: 1px solid #d1d5db;
            display: flex; align-items: flex-start; width: 160px; min-width: 160px; font-size: 14px;
        }
        .content { padding: 12px; flex: 1; font-size: 14px; text-align: left; }
        .content-split { display: flex; flex: 1; }
        .content-main { padding: 12px; border-right: 1px solid #d1d5db; flex: 1; font-size: 14px; text-align: left; }
        .content-side { padding: 12px; width: 220px; min-width: 220px; font-size: 14px; text-align: left; }
        .content-side-header { font-weight: bold; margin-bottom: 8px; font-size: 12px; }
        .black-header { background-color: #000; color: white; font-weight: bold; text-align: center; padding: 12px; font-size: 14px; }
        .sub-header-row { display: flex; background-color: #f9fafb; border-bottom: 1px solid #d1d5db; min-height: 48px; }
        .sub-header { padding: 12px; font-weight: bold; font-size: 12px; border-right: 1px solid #d1d5db; display: flex; align-items: center; }
        .duration-header { width: 80px; min-width: 80px; justify-content: center; }
        .activity-header { flex: 1; }
        .materials-header { width: 220px; min-width: 220px; justify-content: center; }
        .activity-row { display: flex; min-height: 80px; border-bottom: 1px solid #d1d5db; }
        .duration-cell {
            width: 80px; min-width: 80px; padding: 12px; border-right: 1px solid #d1d5db;
            display: flex; align-items: flex-start; justify-content: center; font-size: 14px;
        }
        .activity-cell { flex: 1; padding: 12px; border-right: 1px solid #d1d5db; font-size: 14px; text-align: left; }
        .materials-cell { width: 220px; min-width: 220px; padding: 12px; font-size: 14px; text-align: left; }
        .reference-note { margin-top: 8px; font-size: 12px; color: #4b5563; }
        .activity-half { margin-bottom: 10px; }
        .activity-half:last-child { margin-bottom: 0; }
        .activity-half-learner { border-top: 1px dotted #cbd5e1; padding-top: 8px; }
        .activity-half-label {
            display: block; font-size: 11px; font-weight: 700; text-transform: uppercase;
            letter-spacing: 0.05em; color: #6b7280; margin-bottom: 2px;
        }
        .activity-title { font-weight: 700; font-size: 14px; color: #1f2937; margin-bottom: 6px; }
        .total-row { background-color: #f9fafb; min-height: 0; }
        .total-row .duration-cell { text-align: center; font-size: 13px; align-items: center; }
        .total-caption { display: flex; align-items: center; font-size: 12px; color: #6b7280; }
        .reference-appendix { max-width: 1200px; margin: 24px auto 0; background: white; border-radius: 8px; padding: 24px; box-shadow: 0 4px 6px rgba(0,0,0,0.1); }
        .reference-appendix h2 { font-size: 20px; margin-bottom: 4px; color: #1f2937; }
        .reference-intro { font-size: 13px; color: #6b7280; margin-bottom: 16px; }
        .reference-item { border-top: 1px solid #e5e7eb; padding-top: 16px; margin-top: 16px; }
        .reference-item h3 { font-size: 15px; color: #1f2937; }
        .reference-meta { font-size: 12px; color: #6b7280; margin-bottom: 8px; }
        .reference-text {
            white-space: pre-wrap; font-family: ui-monospace, Menlo, Consolas, monospace;
            font-size: 12px; line-height: 1.5; background: #f9fafb; border: 1px solid #e5e7eb;
            border-radius: 6px; padding: 12px; overflow-x: auto;
        }
        .reference-missing { font-size: 13px; color: #b45309; font-style: italic; }
        .reference-link { font-size: 13px; margin-bottom: 8px; word-break: break-all; }
        .reference-link a { color: #1d4ed8; }
${richContentCss(1.4)}
        @media print {
            body { background-color: white; padding: 0; }
            .container, .reference-appendix { box-shadow: none; border-radius: 0; }
            .reference-appendix { page-break-before: always; }
        }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <h1>${escapeHtml(title)}</h1>
        </div>

        <div class="table-container">
            <div class="row">
                <div class="section-header">Lesson Topic</div>
                <div class="content rich">${escapeHtml(lessonData.lessonTopic || '')}</div>
            </div>

            <div class="row">
                <div class="section-header">Bridge-In</div>
                <div class="content rich">${renderField(lessonData, 'bridgeIn')}</div>
            </div>

            <div class="row">
                <div class="section-header">Objective(s)</div>
                <div class="content-split">
                    <div class="content-main rich">${renderField(lessonData, 'objective')}</div>
                    <div class="content-side">
                        <div class="content-side-header">Pre-Assessment</div>
                        <div class="rich">${renderField(lessonData, 'preAssessment')}</div>
                    </div>
                </div>
            </div>

            <div class="black-header">Presentation/Practice/Participation</div>

            <div class="sub-header-row">
                <div class="sub-header duration-header">Duration (in min.)</div>
                <div class="sub-header activity-header">${handout ? 'Learner Activity' : 'Facilitator or Learner Activity (add rows as needed)'}</div>
                <div class="sub-header materials-header">Materials Required</div>
            </div>
${activities.map((activity) => `
            <div class="activity-row">
                <div class="duration-cell">${escapeHtml(activity.duration || '')}</div>
                <div class="activity-cell">${activityTitle(activity)}${activityHalves(activity, options)}${referenceLine(activity, options)}</div>
                <div class="materials-cell">${materialsHalves(activity, options)}</div>
            </div>`).join('')}

            <div class="activity-row total-row">
                <div class="duration-cell">
                    <div><strong>Total</strong><br>${escapeHtml(formatTotalActivityTime(activities))}</div>
                </div>
                <div class="activity-cell total-caption">Total planned activity time</div>
                <div class="materials-cell"></div>
            </div>

            <div class="row">
                <div class="section-header">Post-Assessment</div>
                <div class="content-split">
                    <div class="content-main rich">${renderField(lessonData, 'postAssessment')}</div>
                    <div class="content-side">
                        <div class="content-side-header">Summary</div>
                        <div class="rich">${renderField(lessonData, 'summary')}</div>
                    </div>
                </div>
            </div>

${handout ? '' : `
            <div class="row">
                <div class="section-header">Reflections on the Lesson</div>
                <div class="content rich">${renderField(lessonData, 'reflections')}</div>
            </div>`}
        </div>
    </div>
${handout ? '' : referenceAppendix(activities)}
</body>
</html>`;
};
